/**
 * @module components/symbols/SymbolsPanel
 * @summary Full-screen "Add Custom Symbol" panel — manual canvas code + AI
 *   generation, live preview, add-to-library, category manager, change password.
 */
import { useEffect, useRef, useState } from 'react';
import { registry } from '../../engine/shapes/registry';
import { setCtx } from '../../engine/shapes/drawFns';
import { useUiStore } from '../../state/uiStore';
import {
  getAllCategories,
  addCustomCat,
  deleteCustomCat,
  isCustomCat,
  getDisplayName,
  renameCat,
  isCatActive,
  toggleCatActive,
  moveCatUp,
  moveCatDown,
} from '../../lib/categories';
import { generateSymbolCode, saveApiKey, loadApiKey } from '../../lib/anthropic';
import { changeSymbolPassword } from '../../lib/symbolAccess';
import type { Shape } from '../../types';

interface Props {
  onClose: () => void;
}

type Status = { type: '' | 'ok' | 'err' | 'info'; msg: string };

export function SymbolsPanel({ onClose }: Props) {
  const bumpLibrary = useUiStore((s) => s.bumpLibrary);
  const previewRef = useRef<HTMLCanvasElement>(null);

  // Symbol properties.
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [cats, setCats] = useState<string[]>(() => getAllCategories());
  const [category, setCategory] = useState(cats[0] || 'Annotations');
  const [w, setW] = useState(60);
  const [h, setH] = useState(40);
  const [fill, setFill] = useState('#ffffff');
  const [stroke, setStroke] = useState('#1a1a1a');

  const [tab, setTab] = useState<'manual' | 'ai'>('manual');
  const [drawCode, setDrawCode] = useState('');
  const [validCode, setValidCode] = useState('');
  const [status, setStatus] = useState<Status>({
    type: 'info',
    msg: 'Configure the symbol, add canvas drawing code, then click Run & Preview.',
  });

  // AI tab.
  const [apiKey, setApiKey] = useState(() => loadApiKey());
  const [aiDesc, setAiDesc] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePrev, setImagePrev] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiStatus, setAiStatus] = useState<Status>({ type: '', msg: '' });
  const [generated, setGenerated] = useState('');

  // Sub-panels.
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [showChPwd, setShowChPwd] = useState(false);

  // Category manager edit state: tracks which category is being renamed inline.
  const [renamingCat, setRenamingCat] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');

  const refreshCats = () => {
    const list = getAllCategories();
    setCats(list);
    if (!list.includes(category)) setCategory(list[0] || 'Annotations');
  };

  // Draw a preview of arbitrary draw code into the preview canvas.
  function runPreview(src: string) {
    const codeStr = (src || '').trim();
    if (!codeStr) {
      setStatus({ type: 'err', msg: 'No drawing code to run.' });
      return;
    }
    const cv = previewRef.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;
    const pw = cv.width;
    const ph = cv.height;
    const margin = 20;
    const scale = Math.min((pw - margin * 2) / w, (ph - margin * 2) / h, 3);
    const ox = (pw - w * scale) / 2;
    const oy = (ph - h * scale) / 2;
    const s: Shape = {
      id: '_preview',
      type: 'custom',
      x: 0,
      y: 0,
      w,
      h,
      fillColor: fill,
      strokeColor: stroke,
      strokeWidth: 1.5,
      fontSize: 11,
      label: label || 'Symbol',
      tag: '',
      labelPos: 'bottom',
      notes: '',
      zIndex: 0,
    };
    ctx.clearRect(0, 0, pw, ph);
    ctx.save();
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    for (let xi = 0; xi < pw; xi += 20) {
      ctx.beginPath(); ctx.moveTo(xi, 0); ctx.lineTo(xi, ph); ctx.stroke();
    }
    for (let yi = 0; yi < ph; yi += 20) {
      ctx.beginPath(); ctx.moveTo(0, yi); ctx.lineTo(pw, yi); ctx.stroke();
    }
    ctx.restore();
    ctx.save();
    ctx.setTransform(scale, 0, 0, scale, ox, oy);
    try {
      setCtx(ctx, s);
      new Function('c', 's', codeStr)(ctx, s);
      ctx.restore();
      setValidCode(codeStr);
      setStatus({ type: 'ok', msg: 'Preview OK — you can now add it to the library.' });
    } catch (e) {
      ctx.restore();
      ctx.clearRect(0, 0, pw, ph);
      setValidCode('');
      setStatus({ type: 'err', msg: 'Error: ' + (e as Error).message });
    }
  }

  useEffect(() => {
    if (validCode) runPreview(validCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, h, fill, stroke]);

  function addToLibrary() {
    if (!validCode) {
      setStatus({ type: 'err', msg: 'Run a successful preview first.' });
      return;
    }
    const typeCode = code.trim().replace(/\s+/g, '_').toUpperCase();
    if (!typeCode) {
      setStatus({ type: 'err', msg: 'Symbol Code is required.' });
      return;
    }
    if (registry.getDef(typeCode) && !confirm(`A symbol "${typeCode}" exists. Overwrite it?`)) return;
    registry.addCustom(typeCode, { label: label.trim() || typeCode, w, h, g: category, code: validCode });
    bumpLibrary();
    setStatus({ type: 'ok', msg: `Symbol "${label || typeCode}" saved to "${getDisplayName(category)}"!` });
    setTimeout(onClose, 1400);
  }

  async function runGenerate() {
    if (!apiKey.trim()) {
      setAiStatus({ type: 'err', msg: 'Please enter your Anthropic API key.' });
      return;
    }
    saveApiKey(apiKey.trim());
    if (!aiDesc.trim() && !imageFile) {
      setAiStatus({ type: 'err', msg: 'Provide a description and/or a reference image.' });
      return;
    }
    setGenerating(true);
    setAiStatus({ type: 'info', msg: 'Generating drawing code…' });
    try {
      const result = await generateSymbolCode({
        apiKey: apiKey.trim(),
        description: aiDesc.trim(),
        imageFile,
        fill,
        stroke,
        width: w,
        height: h,
        category,
        label: label || 'Symbol',
      });
      setGenerated(result);
      setAiStatus({ type: 'ok', msg: 'Code generated! Review below, then "Use This Code".' });
    } catch (e) {
      setAiStatus({ type: 'err', msg: 'Generation failed: ' + (e as Error).message });
    } finally {
      setGenerating(false);
    }
  }

  function commitRename(original: string) {
    if (!renameVal.trim() || renameVal.trim() === original) {
      setRenamingCat(null);
      return;
    }
    const err = renameCat(original, renameVal);
    if (err) { alert(err); return; }
    setRenamingCat(null);
    refreshCats();
    bumpLibrary();
  }

  return (
    <div className="overlay-panel open">
      <div className="asp-hdr">
        <button className="asp-back-btn" onClick={onClose}>
          ← Back to Canvas
        </button>
        <span className="asp-hdr-title">✚ Add Custom Symbol</span>
        <span className="asp-hdr-hint">Create &amp; register new equipment symbols for the PFD library</span>
        <button className="asp-manage-cats-btn" onClick={() => setShowCatMgr((v) => !v)}>
          ⚙ Manage Categories
        </button>
        <button className="asp-chpwd-btn" onClick={() => setShowChPwd(true)}>
          🔑 Change Password
        </button>
      </div>

      <div className="asp-body">
        {/* LEFT: properties */}
        <div className="asp-card">
          <div className="asp-card-hdr">Symbol Properties</div>
          <div className="asp-grid3" style={{ marginBottom: 12 }}>
            <div className="asp-fld">
              <label className="asp-lbl">Code *</label>
              <input className="asp-inp asp-inp-mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. new_pump" />
              <span style={{ fontSize: 10, color: '#888' }}>Unique key (no spaces)</span>
            </div>
            <div className="asp-fld">
              <label className="asp-lbl">Label (Display Name) *</label>
              <input className="asp-inp" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. New Pump" />
            </div>
          </div>
          <div className="asp-fld" style={{ marginBottom: 12 }}>
            <label className="asp-lbl">Category *</label>
            <select className="asp-sel" value={category} onChange={(e) => setCategory(e.target.value)}>
              {cats.map((c) => (
                <option key={c} value={c}>{getDisplayName(c)}</option>
              ))}
            </select>
          </div>
          <div className="asp-grid2" style={{ marginBottom: 12 }}>
            <div className="asp-fld">
              <label className="asp-lbl">Default Width (px)</label>
              <input className="asp-inp" type="number" min={20} max={400} value={w} onChange={(e) => setW(+e.target.value || 60)} />
            </div>
            <div className="asp-fld">
              <label className="asp-lbl">Default Height (px)</label>
              <input className="asp-inp" type="number" min={20} max={400} value={h} onChange={(e) => setH(+e.target.value || 40)} />
            </div>
          </div>
          <div className="asp-grid2">
            <div className="asp-fld">
              <label className="asp-lbl">Fill Color</label>
              <div className="asp-color-row">
                <input type="color" className="asp-swatch" value={fill} onChange={(e) => setFill(e.target.value)} />
                <input className="asp-inp asp-inp-mono" value={fill} onChange={(e) => setFill(e.target.value)} maxLength={7} />
              </div>
            </div>
            <div className="asp-fld">
              <label className="asp-lbl">Stroke Color</label>
              <div className="asp-color-row">
                <input type="color" className="asp-swatch" value={stroke} onChange={(e) => setStroke(e.target.value)} />
                <input className="asp-inp asp-inp-mono" value={stroke} onChange={(e) => setStroke(e.target.value)} maxLength={7} />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: preview */}
        <div className="asp-card">
          <div className="asp-card-hdr">Symbol Preview</div>
          <div className="asp-preview-box">
            <canvas ref={previewRef} width={320} height={190} />
          </div>
          {status.type && <div className={'asp-status ' + status.type}>{status.msg}</div>}
          <div className="asp-addlib-row">
            <button className="asp-btn asp-btn-success" disabled={!validCode} onClick={addToLibrary}>
              ✚ Add Symbol to Library
            </button>
          </div>
        </div>

        {/* FULL-WIDTH: code input */}
        <div className="asp-card asp-card-full">
          <div className="asp-tabs">
            <button className={'asp-tab' + (tab === 'manual' ? ' active' : '')} onClick={() => setTab('manual')}>
              📝 Manual Canvas Code
            </button>
            <button className={'asp-tab' + (tab === 'ai' ? ' active' : '')} onClick={() => setTab('ai')}>
              🤖 AI Generate
            </button>
          </div>

          {tab === 'manual' ? (
            <>
              <div className="asp-fld" style={{ marginBottom: 10 }}>
                <label className="asp-lbl">
                  Canvas JS Code{' '}
                  <span style={{ color: '#888', fontWeight: 400, textTransform: 'none', fontSize: 10 }}>
                    — body of function(c, s). Use s.x, s.y, s.w, s.h.
                  </span>
                </label>
                <textarea
                  className="asp-ta"
                  value={drawCode}
                  onChange={(e) => setDrawCode(e.target.value)}
                  placeholder={`// Example — a simple rectangle symbol:\nconst {x,y,w,h} = s;\nc.fillStyle = s.fillColor || '#ffffff';\nc.fillRect(x, y, w, h);\nc.strokeRect(x, y, w, h);`}
                />
              </div>
              <div className="asp-btn-row">
                <button className="asp-btn asp-btn-primary" onClick={() => runPreview(drawCode)}>
                  ▶ Run &amp; Preview
                </button>
                <button
                  className="asp-btn asp-btn-ghost"
                  onClick={() => { setDrawCode(''); setValidCode(''); setStatus({ type: 'info', msg: 'Code cleared.' }); }}
                >
                  ✕ Clear
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="asp-grid2" style={{ marginBottom: 14 }}>
                <div className="asp-fld">
                  <label className="asp-lbl">Anthropic API Key</label>
                  <input className="asp-inp asp-inp-mono" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-ant-api03-…" />
                  <span className="asp-apikey-hint">
                    Used only in this browser.{' '}
                    <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" style={{ color: '#0078d4' }}>
                      Get yours here
                    </a>
                  </span>
                </div>
                <div className="asp-fld">
                  <label className="asp-lbl">Reference Image (optional)</label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    style={{ fontSize: 11 }}
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setImageFile(f);
                      if (f) {
                        const r = new FileReader();
                        r.onload = (ev) => setImagePrev(String(ev.target?.result));
                        r.readAsDataURL(f);
                      } else setImagePrev('');
                    }}
                  />
                  {imagePrev && <img className="asp-img-prev" src={imagePrev} alt="reference" />}
                </div>
              </div>
              <div className="asp-fld" style={{ marginBottom: 12 }}>
                <label className="asp-lbl">Symbol Description / Prompt</label>
                <textarea className="asp-ta asp-ta-plain" value={aiDesc} onChange={(e) => setAiDesc(e.target.value)} placeholder="Describe the equipment symbol in detail…" />
              </div>
              <div className="asp-btn-row">
                <button className="asp-btn asp-btn-primary" disabled={generating} onClick={runGenerate}>
                  🤖 Generate Symbol Code
                </button>
                {generating && <span style={{ fontSize: 11, color: '#555' }}>⏳ Generating…</span>}
              </div>
              {aiStatus.type && <div className={'asp-status ' + aiStatus.type}>{aiStatus.msg}</div>}
              {generated && (
                <div style={{ marginTop: 16 }}>
                  <div className="asp-fld" style={{ marginBottom: 10 }}>
                    <label className="asp-lbl">Generated Code — review &amp; edit</label>
                    <textarea className="asp-ta" style={{ minHeight: 220 }} value={generated} onChange={(e) => setGenerated(e.target.value)} />
                  </div>
                  <div className="asp-btn-row">
                    <button
                      className="asp-btn asp-btn-primary"
                      onClick={() => { setDrawCode(generated); setTab('manual'); runPreview(generated); }}
                    >
                      ↑ Use This Code &amp; Preview
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Category manager */}
        {showCatMgr && (
          <div className="asp-card asp-card-full">
            <div className="asp-card-hdr" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>⚙ Manage Categories</span>
              <button className="asp-btn asp-btn-ghost" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => setShowCatMgr(false)}>
                ✕ Close
              </button>
            </div>

            {/* Add new category */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'flex-end' }}>
              <div className="asp-fld" style={{ flex: 1 }}>
                <label className="asp-lbl">New Category Name</label>
                <input
                  className="asp-inp"
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { const err = addCustomCat(newCat); if (err) { alert(err); return; } setNewCat(''); refreshCats(); bumpLibrary(); } }}
                  placeholder="e.g. Heat Transfer Equipment"
                />
              </div>
              <button
                className="asp-btn asp-btn-primary"
                style={{ height: 32 }}
                onClick={() => {
                  const err = addCustomCat(newCat);
                  if (err) { alert(err); return; }
                  setNewCat('');
                  refreshCats();
                  bumpLibrary();
                }}
              >
                + Add
              </button>
            </div>

            <p style={{ fontSize: 10, color: '#888', marginBottom: 10 }}>
              Tip: Use the arrow buttons to reorder. Click the name to rename. Toggle the eye to hide/show in the library.
            </p>

            <div className="asp-cat-list">
              {cats.map((c, idx) => {
                const active = isCatActive(c);
                const custom = isCustomCat(c);
                const isRenaming = renamingCat === c;
                return (
                  <div className={'asp-cat-item' + (active ? '' : ' asp-cat-item-inactive')} key={c}>
                    {/* Move up/down */}
                    <div className="asp-cat-order-btns">
                      <button
                        className="asp-cat-ibt move"
                        disabled={idx === 0}
                        title="Move up"
                        onClick={() => { moveCatUp(c, cats); refreshCats(); bumpLibrary(); }}
                      >▲</button>
                      <button
                        className="asp-cat-ibt move"
                        disabled={idx === cats.length - 1}
                        title="Move down"
                        onClick={() => { moveCatDown(c, cats); refreshCats(); bumpLibrary(); }}
                      >▼</button>
                    </div>

                    {/* Name / rename input */}
                    {isRenaming ? (
                      <input
                        className="asp-cat-rename-inp"
                        autoFocus
                        value={renameVal}
                        onChange={(e) => setRenameVal(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(c);
                          if (e.key === 'Escape') setRenamingCat(null);
                        }}
                        onBlur={() => commitRename(c)}
                      />
                    ) : (
                      <span
                        className="asp-cat-item-name"
                        title="Click to rename"
                        style={{ cursor: 'pointer', flex: 1 }}
                        onClick={() => { setRenamingCat(c); setRenameVal(getDisplayName(c)); }}
                      >
                        {getDisplayName(c)}
                      </span>
                    )}

                    {/* Built-in / custom badge */}
                    <span className="asp-cat-badge">{custom ? 'custom' : 'built-in'}</span>

                    {/* Active toggle */}
                    <button
                      className={'asp-cat-ibt toggle ' + (active ? 'on' : 'off')}
                      title={active ? 'Visible in library — click to hide' : 'Hidden in library — click to show'}
                      onClick={() => { toggleCatActive(c); refreshCats(); bumpLibrary(); }}
                    >
                      {active ? '👁' : '🚫'}
                    </button>

                    {/* Delete (custom only) */}
                    {custom && (
                      <button
                        className="asp-cat-ibt del"
                        onClick={() => {
                          if (!confirm(`Delete category "${getDisplayName(c)}"? Symbols in it will remain but lose their category grouping.`)) return;
                          deleteCustomCat(c);
                          refreshCats();
                          bumpLibrary();
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showChPwd && <ChangePasswordModal onClose={() => setShowChPwd(false)} />}
    </div>
  );
}

/** Change the symbol-library access password. */
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [cfm, setCfm] = useState('');
  const [err, setErr] = useState('');
  const save = () => {
    const e = changeSymbolPassword(cur, nw, cfm);
    if (e) { setErr(e); return; }
    alert('Password changed successfully.');
    onClose();
  };
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="sym-pwd-icon">🔑</div>
        <div className="sym-pwd-title">Change Password</div>
        <div className="sym-pwd-sub">Update the Symbol Library access password</div>
        <input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
        <input type="password" placeholder="New password" value={nw} onChange={(e) => setNw(e.target.value)} />
        <input type="password" placeholder="Confirm new password" value={cfm} onChange={(e) => setCfm(e.target.value)} />
        <div className="sym-pwd-err">{err}</div>
        <div className="sym-pwd-row">
          <button className="sym-pwd-btn primary" onClick={save}>Save</button>
          <button className="sym-pwd-btn ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
